import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, Check, ExternalLink } from 'lucide-react';
const languages = [{
  id: 'curl',
  label: 'cURL'
}, {
  id: 'node',
  label: 'Node.js'
}, {
  id: 'python',
  label: 'Python'
}, {
  id: 'go',
  label: 'Go'
}, {
  id: 'java',
  label: 'Java'
}, {
  id: 'php',
  label: 'PHP'
}, {
  id: 'ruby',
  label: 'Ruby'
}, {
  id: 'dotnet',
  label: '.NET'
}];
const pingExamples: Record<string, string> = {
  curl: `curl https://api.obseq.com/v1/ping \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
  node: `const response = await fetch('https://api.obseq.com/v1/ping', {
  headers: { 'Authorization': \`Bearer \${apiKey}\` }
});
const data = await response.json();
console.log(data);`,
  python: `import requests

response = requests.get(
    'https://api.obseq.com/v1/ping',
    headers={'Authorization': f'Bearer {api_key}'}
)
print(response.json())`,
  go: `req, _ := http.NewRequest("GET", "https://api.obseq.com/v1/ping", nil)
req.Header.Set("Authorization", "Bearer "+apiKey)

client := &http.Client{}
resp, _ := client.Do(req)
defer resp.Body.Close()`,
  java: `HttpRequest request = HttpRequest.newBuilder()
    .uri(URI.create("https://api.obseq.com/v1/ping"))
    .header("Authorization", "Bearer " + apiKey)
    .GET()
    .build();

HttpClient client = HttpClient.newHttpClient();
HttpResponse<String> response = client.send(request, 
    HttpResponse.BodyHandlers.ofString());`,
  php: `$response = $client->get('https://api.obseq.com/v1/ping', [
    'headers' => ['Authorization' => 'Bearer ' . $apiKey]
]);
echo $response->getBody();`,
  ruby: `response = HTTParty.get(
  'https://api.obseq.com/v1/ping',
  headers: { 'Authorization' => "Bearer #{api_key}" }
)
puts response.parsed_response`,
  dotnet: `var client = new HttpClient();
client.DefaultRequestHeaders.Authorization = 
    new AuthenticationHeaderValue("Bearer", apiKey);

var response = await client.GetAsync(
    "https://api.obseq.com/v1/ping");
var data = await response.Content.ReadAsStringAsync();`
};
const payoutExamples: Record<string, string> = {
  curl: `curl -X POST https://api.obseq.com/v1/payments/payouts \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -d '{
    "amount": 250000,
    "currency": "USD",
    "beneficiary_id": "bn_01J9Z9GJ9Q2TQW3R4E5Y6U7I8O",
    "purpose": "supplier_payment"
  }'`,
  node: `import { randomUUID } from 'crypto';

const response = await fetch('https://api.obseq.com/v1/payments/payouts', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json',
    'Idempotency-Key': randomUUID()
  },
  body: JSON.stringify({
    amount: 250000,
    currency: 'USD',
    beneficiary_id: 'bn_01J9Z9GJ9Q2TQW3R4E5Y6U7I8O',
    purpose: 'supplier_payment'
  })
});

const payout = await response.json();`,
  python: `import uuid, requests

response = requests.post(
    'https://api.obseq.com/v1/payments/payouts',
    headers={
        'Authorization': f'Bearer {api_key}',
        'Content-Type': 'application/json',
        'Idempotency-Key': str(uuid.uuid4())
    },
    json={
        'amount': 250000,
        'currency': 'USD',
        'beneficiary_id': 'bn_01J9Z9GJ9Q2TQW3R4E5Y6U7I8O',
        'purpose': 'supplier_payment'
    }
)
payout = response.json()`,
  go: `import "github.com/google/uuid"

payload := map[string]interface{}{
    "amount": 250000,
    "currency": "USD",
    "beneficiary_id": "bn_01J9Z9GJ9Q2TQW3R4E5Y6U7I8O",
    "purpose": "supplier_payment",
}

body, _ := json.Marshal(payload)
req, _ := http.NewRequest("POST", 
    "https://api.obseq.com/v1/payments/payouts", 
    bytes.NewBuffer(body))
req.Header.Set("Authorization", "Bearer "+apiKey)
req.Header.Set("Idempotency-Key", uuid.New().String())

client := &http.Client{}
resp, _ := client.Do(req)`,
  java: `import java.util.UUID;

String json = """
{
  "amount": 250000,
  "currency": "USD",
  "beneficiary_id": "bn_01J9Z9GJ9Q2TQW3R4E5Y6U7I8O",
  "purpose": "supplier_payment"
}
""";

HttpRequest request = HttpRequest.newBuilder()
    .uri(URI.create("https://api.obseq.com/v1/payments/payouts"))
    .header("Authorization", "Bearer " + apiKey)
    .header("Idempotency-Key", UUID.randomUUID().toString())
    .POST(HttpRequest.BodyPublishers.ofString(json))
    .build();`,
  php: `use Ramsey\\Uuid\\Uuid;

$response = $client->post('https://api.obseq.com/v1/payments/payouts', [
    'headers' => [
        'Authorization' => 'Bearer ' . $apiKey,
        'Idempotency-Key' => Uuid::uuid4()->toString()
    ],
    'json' => [
        'amount' => 250000,
        'currency' => 'USD',
        'beneficiary_id' => 'bn_01J9Z9GJ9Q2TQW3R4E5Y6U7I8O',
        'purpose' => 'supplier_payment'
    ]
]);`,
  ruby: `require 'securerandom'

response = HTTParty.post(
  'https://api.obseq.com/v1/payments/payouts',
  headers: {
    'Authorization' => "Bearer #{api_key}",
    'Idempotency-Key' => SecureRandom.uuid
  },
  body: {
    amount: 250000,
    currency: 'USD',
    beneficiary_id: 'bn_01J9Z9GJ9Q2TQW3R4E5Y6U7I8O',
    purpose: 'supplier_payment'
  }.to_json
)`,
  dotnet: `var payload = new {
    amount = 250000,
    currency = "USD",
    beneficiary_id = "bn_01J9Z9GJ9Q2TQW3R4E5Y6U7I8O",
    purpose = "supplier_payment"
};

var request = new HttpRequestMessage(HttpMethod.Post, 
    "https://api.obseq.com/v1/payments/payouts");
request.Headers.Add("Authorization", $"Bearer {apiKey}");
request.Headers.Add("Idempotency-Key", Guid.NewGuid().ToString());
request.Content = JsonContent.Create(payload);

var response = await client.SendAsync(request);`
};
export function QuickstartSection() {
  const [activeLanguage, setActiveLanguage] = useState('curl');
  const [activeExample, setActiveExample] = useState<'ping' | 'payout'>('ping');
  const [copiedPing, setCopiedPing] = useState(false);
  const [copiedPayout, setCopiedPayout] = useState(false);
  const handleCopy = (type: 'ping' | 'payout') => {
    const code = type === 'ping' ? pingExamples[activeLanguage] : payoutExamples[activeLanguage];
    navigator.clipboard.writeText(code);
    if (type === 'ping') {
      setCopiedPing(true);
      setTimeout(() => setCopiedPing(false), 2000);
    } else {
      setCopiedPayout(true);
      setTimeout(() => setCopiedPayout(false), 2000);
    }
  };
  return <section className="py-20 bg-white" data-id="element-537">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-538">
        <div className="max-w-5xl mx-auto" data-id="element-539">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10 sm:mb-12" data-id="element-540">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" data-id="element-541">
              Make your first call
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-542">
              Test the API with a simple ping request, then create a payout.
            </p>
          </motion.div>

          {/* Example 1: Ping */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="mb-12" data-id="element-543">
            <h3 className="text-xl font-semibold text-slate-900 mb-4" data-id="element-544">
              1. Test Your API Key
            </h3>
            <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden" data-id="element-545">
              {/* Language Tabs */}
              <div className="flex items-center gap-1 px-4 pt-4 overflow-x-auto" data-id="element-546">
                {languages.map(lang => <button key={lang.id} onClick={() => setActiveLanguage(lang.id)} className={`px-4 py-2 text-sm font-medium rounded-t transition-colors whitespace-nowrap ${activeLanguage === lang.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`} data-id="element-547">
                    {lang.label}
                  </button>)}
              </div>

              {/* Code Block */}
              <div className="relative bg-slate-900 p-6" data-id="element-548">
                <pre className="text-sm text-slate-100 font-mono overflow-x-auto" data-id="element-549">
                  <code data-id="element-550">{pingExamples[activeLanguage]}</code>
                </pre>

                {/* Copy Button */}
                <button onClick={() => handleCopy('ping')} className="absolute top-4 right-4 p-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors" title="Copy code" data-id="element-551">
                  {copiedPing ? <Check className="w-4 h-4" data-id="element-552" /> : <Copy className="w-4 h-4" data-id="element-553" />}
                </button>
              </div>

              {/* Footer */}
              <div className="px-4 sm:px-6 py-4 bg-white border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2" data-id="element-554">
                <span className="text-sm text-slate-600" data-id="element-555">
                  Endpoint:{' '}
                  <code className="text-xs font-mono bg-slate-100 px-2 py-1 rounded" data-id="element-556">
                    GET /v1/ping
                  </code>
                </span>
                <a href="#/docs/api-reference" className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-557">
                  View in Docs
                  <ExternalLink className="w-3 h-3" data-id="element-558" />
                </a>
              </div>
            </div>

            {/* Expected Response */}
            <div className="mt-4" data-id="element-559">
              <p className="text-sm font-medium text-slate-700 mb-2" data-id="element-560">
                Expected Response:
              </p>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm overflow-x-auto" data-id="element-561">
                <code data-id="element-562">{`{
  "status": "ok",
  "timestamp": "2026-01-03T18:41:22Z"
}`}</code>
              </pre>
            </div>
          </motion.div>

          {/* Example 2: Create Payout */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} data-id="element-563">
            <h3 className="text-xl font-semibold text-slate-900 mb-4" data-id="element-564">
              2. Create a Payout
            </h3>
            <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden" data-id="element-565">
              {/* Language Tabs */}
              <div className="flex items-center gap-1 px-4 pt-4 overflow-x-auto" data-id="element-566">
                {languages.map(lang => <button key={lang.id} onClick={() => setActiveLanguage(lang.id)} className={`px-4 py-2 text-sm font-medium rounded-t transition-colors whitespace-nowrap ${activeLanguage === lang.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`} data-id="element-567">
                    {lang.label}
                  </button>)}
              </div>

              {/* Code Block */}
              <div className="relative bg-slate-900 p-6" data-id="element-568">
                <pre className="text-sm text-slate-100 font-mono overflow-x-auto" data-id="element-569">
                  <code data-id="element-570">{payoutExamples[activeLanguage]}</code>
                </pre>

                {/* Copy Button */}
                <button onClick={() => handleCopy('payout')} className="absolute top-4 right-4 p-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors" title="Copy code" data-id="element-571">
                  {copiedPayout ? <Check className="w-4 h-4" data-id="element-572" /> : <Copy className="w-4 h-4" data-id="element-573" />}
                </button>
              </div>

              {/* Footer */}
              <div className="px-4 sm:px-6 py-4 bg-white border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2" data-id="element-574">
                <span className="text-sm text-slate-600" data-id="element-575">
                  Endpoint:{' '}
                  <code className="text-xs font-mono bg-slate-100 px-2 py-1 rounded" data-id="element-576">
                    POST /v1/payments/payouts
                  </code>
                </span>
                <a href="#/docs/api-reference/payments" className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-577">
                  View in Docs
                  <ExternalLink className="w-3 h-3" data-id="element-578" />
                </a>
              </div>
            </div>

            {/* Expected Response */}
            <div className="mt-4" data-id="element-579">
              <p className="text-sm font-medium text-slate-700 mb-2" data-id="element-580">
                Expected Response:
              </p>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm overflow-x-auto" data-id="element-581">
                <code data-id="element-582">{`{
  "id": "po_01J9Z98K5K8C0MZ9QK3G4B1E2P",
  "object": "payments.payout",
  "status": "pending",
  "amount": 250000,
  "currency": "USD",
  "beneficiary_id": "bn_01J9Z9GJ9Q2TQW3R4E5Y6U7I8O",
  "created_at": "2026-01-03T18:41:22Z"
}`}</code>
              </pre>
            </div>
          </motion.div>

          {/* CTA to Docs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="mt-12 text-center" data-id="element-583">
            <a href="#/docs" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors" data-id="element-584">
              Explore Full Documentation
              <ExternalLink className="w-4 h-4" data-id="element-585" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}